/**
 * ImageContent - Imagem expandível com Lightbox (zoom, rotação, pan, download)
 * Inclui retry automático para URLs que ainda não estão prontas (ex: S3)
 * Reutiliza ImageLightbox — mesmo padrão do FilesPanel.
 */
import { useState, useCallback, useRef, useEffect } from "react";
import { DialogTrigger } from "@/components/ui/dialog";
import { MediaLightbox } from "@/components/ui/media-lightbox";
import { ImageOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";

const MAX_RETRIES = 4;
const RETRY_DELAYS = [2000, 4000, 8000, 15000]; // Backoff progressivo

export function ImageContent({ src, thumb, caption, className }) {
  // A legenda segue o MESMO sinal de tema que pinta o fundo da bolha
  // (WhatsAppBubble/TextContent também usam `useTheme`). Com token semântico
  // puro ou variante `dark:` ela dependeria da classe `dark` no <html>, e
  // qualquer divergência entre os dois deixava texto claro sobre bolha branca.
  const { resolved: currentTheme } = useTheme();
  const isDark = currentTheme === "dark";
  const [isOpen, setIsOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [retryKey, setRetryKey] = useState(0);
  const timerRef = useRef(null);
  const displaySrc = thumb || src;

  // Limpa timer ao desmontar
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleError = useCallback(() => {
    if (retryCount < MAX_RETRIES) {
      // Agendar retry com backoff — usa key para forçar remount sem alterar URL
      timerRef.current = setTimeout(() => {
        setRetryKey((prev) => prev + 1);
        setRetryCount((prev) => prev + 1);
        setHasError(false);
      }, RETRY_DELAYS[retryCount] || 15000);
    }
    setHasError(true);
  }, [retryCount]);

  const handleLoad = useCallback(() => {
    setHasError(false);
    setRetryCount(0);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const handleManualRetry = useCallback(() => {
    setRetryKey((prev) => prev + 1);
    setRetryCount(0);
    setHasError(false);
  }, []);

  return (
    <MediaLightbox
      open={isOpen}
      onOpenChange={setIsOpen}
      src={src}
      title={caption}
    >
      <DialogTrigger asChild>
        <div className={cn("cursor-pointer", className)}>
          <div
            className={cn(
              "rounded-lg overflow-hidden",
              "max-w-[220px] max-h-[280px]",
            )}
          >
            {hasError && retryCount >= MAX_RETRIES ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleManualRetry();
                }}
                className="w-full flex flex-col items-center justify-center gap-2 py-8 px-4 bg-surface-active/50 rounded-lg text-foreground-muted hover:text-foreground-secondary hover:bg-surface-active transition-colors"
                style={{ minHeight: "120px" }}
              >
                <ImageOff className="w-8 h-8 opacity-50" />
                <span className="text-[11px]">Toque para recarregar</span>
              </button>
            ) : (
              <img
                key={retryKey}
                src={displaySrc}
                alt={caption || "Imagem"}
                className={cn(
                  "w-full h-full object-cover hover:opacity-90 transition-opacity max-h-[260px]",
                  hasError && "opacity-0 h-0",
                )}
                loading="lazy"
                style={{ minHeight: hasError ? "0" : "120px" }}
                onError={handleError}
                onLoad={handleLoad}
              />
            )}
            {hasError && retryCount < MAX_RETRIES && (
              <div
                className="w-full flex items-center justify-center py-8 bg-surface-active/30 rounded-lg"
                style={{ minHeight: "120px" }}
              >
                <div className="w-5 h-5 border-2 border-foreground-muted/30 border-t-foreground-muted rounded-full animate-spin" />
              </div>
            )}
          </div>
          {caption && (
            <p
              className={cn(
                "text-sm whitespace-pre-wrap break-words mt-1 px-1 max-w-[220px]",
                isDark ? "text-foreground-strong" : "text-gray-800",
              )}
            >
              {caption}
            </p>
          )}
        </div>
      </DialogTrigger>
    </MediaLightbox>
  );
}
